import { forwardRef } from 'react';
import { cn } from '../utils/cn';
import { Icon, type IconName } from '../primitives/Icon';

export type CalloutTone = 'info' | 'success' | 'warning' | 'attention' | 'danger' | 'neutral';

const toneClasses: Record<CalloutTone, { wrap: string; icon: string; defaultIcon: IconName }> = {
  info: { wrap: 'border-info-subtle bg-info-subtle', icon: 'text-info', defaultIcon: 'info' },
  success: {
    wrap: 'border-success-subtle bg-success-subtle',
    icon: 'text-success',
    defaultIcon: 'check-circle',
  },
  warning: {
    wrap: 'border-warning-subtle bg-warning-subtle',
    icon: 'text-warning',
    defaultIcon: 'alert-circle',
  },
  attention: {
    wrap: 'border-attention-subtle bg-attention-subtle',
    icon: 'text-attention',
    defaultIcon: 'alert',
  },
  danger: {
    wrap: 'border-danger-subtle bg-danger-subtle',
    icon: 'text-danger',
    defaultIcon: 'alert',
  },
  neutral: {
    wrap: 'border-line bg-surface-sunken',
    icon: 'text-content-muted',
    defaultIcon: 'info',
  },
};

export interface CalloutProps extends React.HTMLAttributes<HTMLDivElement> {
  tone?: CalloutTone;
  title: string;
  icon?: IconName;
  actions?: React.ReactNode;
  /** `alert` for conditions the user must act on; `status` for informational. */
  live?: 'alert' | 'status' | 'none';
}

export const Callout = forwardRef<HTMLDivElement, CalloutProps>(function Callout(
  { tone = 'info', title, icon, actions, live = 'status', className, children, ...rest },
  ref,
) {
  const meta = toneClasses[tone];
  return (
    <div
      ref={ref}
      role={live === 'none' ? undefined : live === 'alert' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-lg border px-3.5 py-3 text-dense-lg',
        meta.wrap,
        className,
      )}
      {...rest}
    >
      <Icon name={icon ?? meta.defaultIcon} size={16} className={cn('mt-0.5', meta.icon)} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <p className="font-semibold text-content">{title}</p>
        {children ? <div className="text-content-secondary">{children}</div> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
});
